<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { ReactiveImage } from '@/libraries/vue-ractive-new-html-image/ref-img';

const VALID_BLURHASH = 'LEHV6nWB2yk8pyo0adR*.7kCMdnj';

const thumbnailSvg = makeSvgDataUrl(
  480,
  320,
  ['#30475e', '#1b262c'],
  'Thumbnail',
);
const fullSvg = makeSvgDataUrl(
  1280,
  853,
  ['#ff7e5f', '#feb47b'],
  'Full Image',
);
const replacementSvg = makeSvgDataUrl(
  1280,
  853,
  ['#118ab2', '#06d6a0'],
  'Replacement Image',
);

const image = ref<ReactiveImage | null>(null);
const eventLog = ref<string[]>([]);
const pendingTimer = ref<number | null>(null);

const previewSrc = computed(() => image.value?.displaySrc.value ?? '');
const loaded = computed(() => image.value?.loaded.value ?? false);
const currentSrc = computed(() => image.value?.currentSrc ?? '');

watch(
  () => image.value?.displaySrc.value ?? '',
  (value, previous) => {
    if (value === previous) return;
    appendLog(`displaySrc changed to ${describeSrc(value)}`);
  },
);

watch(
  loaded,
  (value, previous) => {
    if (value === previous) return;
    appendLog(`loaded changed to ${String(value)}`);
  },
);

setupBlurhashOnly();

onBeforeUnmount(() => {
  clearPendingTimer();
});

function setupBlurhashOnly() {
  resetImage(
    new ReactiveImage(480, 320, VALID_BLURHASH),
    'Created blurhash-only image.',
  );
}

function setupBlurhashWithThumbnail() {
  resetImage(
    new ReactiveImage(480, 320, VALID_BLURHASH, thumbnailSvg),
    'Created image with blurhash and thumbnail.',
  );
}

function setupThumbnailOnly() {
  resetImage(
    new ReactiveImage(480, 320, undefined, thumbnailSvg),
    'Created thumbnail-only image.',
  );
}

function loadFullImage(delayMs = 1200, replacement = false) {
  if (!image.value) {
    setupBlurhashWithThumbnail();
  }

  clearPendingTimer();
  appendLog(`Scheduling ${replacement ? 'replacement' : 'full'} image load in ${delayMs}ms.`);
  pendingTimer.value = window.setTimeout(() => {
    if (!image.value) return;
    image.value.src = replacement ? replacementSvg : fullSvg;
    appendLog(`Assigned ${replacement ? 'replacement' : 'full'} image src.`);
    pendingTimer.value = null;
  }, delayMs);
}

function loadBrokenImage(delayMs = 1200) {
  if (!image.value) {
    setupBlurhashWithThumbnail();
  }

  clearPendingTimer();
  appendLog(`Scheduling broken image load in ${delayMs}ms.`);
  pendingTimer.value = window.setTimeout(() => {
    if (!image.value) return;
    image.value.src = '/__reactive_image_test__/missing-image.png';
    appendLog('Assigned broken image src.');
    pendingTimer.value = null;
  }, delayMs);
}

function clearImage() {
  clearPendingTimer();
  image.value = null;
  appendLog('Cleared current image.');
}

function clearLog() {
  eventLog.value = [];
}

function resetImage(nextImage: ReactiveImage, message: string) {
  clearPendingTimer();
  image.value = nextImage;
  appendLog(message);
}

function clearPendingTimer() {
  if (pendingTimer.value !== null) {
    window.clearTimeout(pendingTimer.value);
    pendingTimer.value = null;
  }
}

function appendLog(message: string) {
  const timestamp = new Date().toLocaleTimeString('en-GB', { hour12: false });
  eventLog.value = [`${timestamp} ${message}`, ...eventLog.value].slice(0, 24);
}

function describeSrc(value: string): string {
  if (!value) return '(empty)';
  if (value.startsWith('data:image/svg+xml')) return 'SVG data URL';
  if (value.startsWith('data:image/png')) return 'blurhash PNG data URL';
  if (value.includes('missing-image')) return 'broken image URL';
  return value;
}

function makeSvgDataUrl(
  width: number,
  height: number,
  colors: [string, string],
  label: string,
): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${colors[0]}" />
          <stop offset="100%" stop-color="${colors[1]}" />
        </linearGradient>
      </defs>
      <rect width="${width}" height="${height}" fill="url(#bg)" />
      <circle cx="${Math.round(width * 0.75)}" cy="${Math.round(height * 0.3)}" r="${Math.round(Math.min(width, height) * 0.12)}" fill="rgba(255,255,255,0.16)" />
      <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" fill="#ffffff" font-size="${Math.round(Math.min(width, height) * 0.12)}" font-family="Avenir, Helvetica, Arial, sans-serif">
        ${label}
      </text>
    </svg>
  `.trim();

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}
</script>

<template>
  <main class="reactive-image-test">
    <section class="hero">
      <p class="eyebrow">
        Test Route
      </p>
      <h1>ReactiveImage</h1>
      <p class="lede">
        Isolated single-image playground for checking placeholder, thumbnail, loaded, and error states.
      </p>
    </section>

    <section class="controls panel">
      <button @click="setupBlurhashOnly">
        Blurhash Only
      </button>
      <button @click="setupThumbnailOnly">
        Thumb Only
      </button>
      <button @click="setupBlurhashWithThumbnail">
        Blurhash + Thumbnail
      </button>
      <button @click="loadFullImage()">
        Load Full
      </button>
      <button @click="loadFullImage(1200, true)">
        Replace Full
      </button>
      <button @click="loadBrokenImage()">
        Trigger Error
      </button>
      <button @click="clearImage">
        Clear Image
      </button>
      <button @click="clearLog">
        Clear Log
      </button>
    </section>

    <section class="workspace">
      <article class="preview panel">
        <div class="preview-frame">
          <img
            v-if="previewSrc"
            :src="previewSrc"
            alt="ReactiveImage preview"
            :class="{ loaded }"
          >
          <div
            v-else
            class="empty"
          >
            No image
          </div>
        </div>
      </article>

      <article class="details panel">
        <dl class="facts">
          <div>
            <dt>`loaded`</dt>
            <dd>{{ loaded }}</dd>
          </div>
          <div>
            <dt>`displaySrc`</dt>
            <dd>{{ describeSrc(previewSrc) }}</dd>
          </div>
          <div>
            <dt>`currentSrc`</dt>
            <dd>{{ describeSrc(currentSrc) }}</dd>
          </div>
          <div>
            <dt>Timer</dt>
            <dd>{{ pendingTimer === null ? "idle" : "pending" }}</dd>
          </div>
        </dl>
      </article>

      <article class="log panel">
        <h2>Event Log</h2>
        <ul v-if="eventLog.length">
          <li
            v-for="entry in eventLog"
            :key="entry"
          >
            {{ entry }}
          </li>
        </ul>
        <p
          v-else
          class="empty-log"
        >
          No events yet.
        </p>
      </article>
    </section>

    <section class="sources panel">
      <h2>Source Images</h2>
      <p class="sources-copy">
        Raw assets used by the buttons above, shown without `ReactiveImage` state logic.
      </p>
      <div class="source-grid">
        <figure>
          <img
            :src="thumbnailSvg"
            alt="Raw thumbnail source"
          >
          <figcaption>Thumbnail source</figcaption>
        </figure>
        <figure>
          <img
            :src="fullSvg"
            alt="Raw full image source"
          >
          <figcaption>Full image source</figcaption>
        </figure>
        <figure>
          <img
            :src="replacementSvg"
            alt="Raw replacement image source"
          >
          <figcaption>Replacement source</figcaption>
        </figure>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
.reactive-image-test {
  min-height: 100vh;
  padding: 24px;
  background:
    radial-gradient(circle at top left, rgba(255, 179, 71, 0.22), transparent 34%),
    radial-gradient(circle at top right, rgba(17, 138, 178, 0.18), transparent 28%),
    linear-gradient(180deg, #f5efe4 0%, #efe7d7 100%);
  color: #1f2933;
}

.hero {
  max-width: 720px;
  margin: 0 auto 20px;
}

.eyebrow {
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.78rem;
  color: #8a5a44;
}

h1 {
  margin: 0;
  font-size: clamp(2.2rem, 5vw, 4rem);
  line-height: 0.95;
}

.lede {
  max-width: 52ch;
  margin: 12px 0 0;
  font-size: 1rem;
}

.panel {
  background: rgba(255, 252, 245, 0.86);
  border: 1px solid rgba(31, 41, 51, 0.12);
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(31, 41, 51, 0.08);
  backdrop-filter: blur(8px);
}

.controls {
  max-width: 1200px;
  margin: 0 auto 20px;
  padding: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

button {
  border: 0;
  border-radius: 999px;
  padding: 10px 14px;
  background: #1f2933;
  color: #fffaf0;
  font: inherit;
  cursor: pointer;
}

button:hover {
  background: #32414d;
}

.workspace {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.4fr) minmax(320px, 0.9fr);
}

.sources {
  max-width: 1200px;
  margin: 20px auto 0;
  padding: 18px;
}

.sources h2 {
  margin: 0;
  font-size: 1rem;
}

.sources-copy {
  margin: 8px 0 0;
  color: rgba(31, 41, 51, 0.72);
}

.source-grid {
  margin-top: 16px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.source-grid figure {
  margin: 0;
  display: grid;
  gap: 10px;
}

.source-grid img {
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: contain;
  border-radius: 14px;
  background: #d8d1c2;
}

.source-grid figcaption {
  font-size: 0.92rem;
}

.preview,
.details,
.log {
  padding: 18px;
}

.preview {
  grid-row: span 2;
}

.preview-frame {
  aspect-ratio: 3 / 2;
  border-radius: 14px;
  overflow: hidden;
  background: #d8d1c2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-frame img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: opacity 0.2s ease;
}

.empty,
.empty-log {
  color: rgba(31, 41, 51, 0.6);
}

.facts {
  margin: 0;
  display: grid;
  gap: 14px;
}

.facts div {
  display: grid;
  gap: 4px;
}

.facts dt {
  font-weight: 700;
}

.facts dd {
  margin: 0;
  word-break: break-word;
}

.log h2 {
  margin: 0 0 12px;
  font-size: 1rem;
}

.log ul {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 8px;
}

@media (max-width: 900px) {
  .reactive-image-test {
    padding: 16px;
  }

  .workspace {
    grid-template-columns: 1fr;
  }

  .preview {
    grid-row: auto;
  }

  .source-grid {
    grid-template-columns: 1fr;
  }
}
</style>
